import { FC } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { Theme } from "@mui/material/styles";

import { AutocompleteFooterProps } from "../types";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardDivider } from "@/dashboard-design-system/Divider";
import { DashboardIcon } from "@/dashboard-design-system/Icon";
import { DashboardTypography } from "@/dashboard-design-system/Typography";

export const AutocompleteFooter: FC<AutocompleteFooterProps> = (props) => {
  if (props.type === "default") {
    return <DefaultFooter {...props} />;
  }

  return <AlternateFooter {...props} />;
};

AutocompleteFooter.displayName = "AutocompleteFooter";

const preventBlur = (e: React.MouseEvent) => e.preventDefault();

const DefaultFooter: FC<Extract<AutocompleteFooterProps, { type: "default" }>> = ({
  onApply,
  onClear,
  applyLabel,
  clearLabel,
  size = "standard",
}) => {
  const { t } = useTranslate();
  const resolvedApplyLabel = applyLabel ?? t("dashboard", "dynamicFilter.apply");
  const resolvedClearLabel = clearLabel ?? t("dashboard", "dynamicFilter.clear");
  const isSmall = size === "small";
  const buttonSize = isSmall ? "small" : "medium";

  return (
    <Box onMouseDown={preventBlur}>
      <DashboardDivider />
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={isSmall ? 0.5 : 1}
        sx={(theme: Theme) => ({
          padding: theme.spacing(isSmall ? 1 : 1.5, 2),
          backgroundColor: theme.palette.surface.neutral.white,
          borderBottomLeftRadius: theme.radius(1),
          borderBottomRightRadius: theme.radius(1),
        })}
      >
        <DashboardButton variant="text" vibe="brand" size={buttonSize} onClick={onClear}>
          {resolvedClearLabel}
        </DashboardButton>
        <DashboardButton variant="tonal" vibe="brand" size={buttonSize} onClick={onApply}>
          {resolvedApplyLabel}
        </DashboardButton>
      </Stack>
    </Box>
  );
};

const AlternateFooter: FC<Extract<AutocompleteFooterProps, { type: "alternate" }>> = ({
  actions,
  size = "standard",
}) => {
  const isSmall = size === "small";

  return (
    <Box
      onMouseDown={preventBlur}
      sx={(theme: Theme) => ({
        backgroundColor: theme.palette.surface.neutral.white,
        borderBottomLeftRadius: theme.radius(1),
        borderBottomRightRadius: theme.radius(1),
      })}
    >
      {actions.map((action, index) => {
        const vibe = action.vibe ?? "brand";

        return (
          <Box key={index}>
            <DashboardDivider />
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              onClick={action.onClick}
              data-test={action["data-test"]}
              sx={(theme: Theme) => ({
                padding: theme.spacing(isSmall ? 0.25 : 0.5, 1.5),
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: theme.palette.surface[vibe].quiet,
                },
              })}
            >
              {action.icon && (
                <DashboardIcon size={isSmall ? "extraSmall" : "small"} vibe={`text-${vibe}-moderate`}>
                  {action.icon}
                </DashboardIcon>
              )}
              <DashboardTypography
                variant={isSmall ? "caption" : "subtitle2"}
                sx={(theme: Theme) => ({
                  color: theme.palette.text[vibe].moderate,
                  padding: theme.spacing(isSmall ? 0.75 : 1, 0),
                })}
              >
                {action.label}
              </DashboardTypography>
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
};
